import React, {
  cloneElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  flip,
  offset,
  Placement,
  shift,
  useFloating,
  arrow,
  useInteractions,
  useHover,
  useFocus,
  useRole,
  useDismiss,
  autoUpdate,
} from '@floating-ui/react';
import styled from 'styled-components';

import { palette } from '../theme';
import { modularScale } from '../utils';

export interface TooltipProps {
  placement?: Placement;
  children: React.ReactElement;
  content: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
}

const Content = styled.div`
  position: absolute;
  background-color: ${palette.grayscale.shade1000};
  color: ${palette.brand.white};
  font-weight: 400;
  padding: ${modularScale()};
  border-radius: 4px;
  font-size: ${modularScale(-1)};
  pointer-events: none;
  width: max-content;
  max-width: 340px;
  z-index: 50;
`;

const Arrow = styled.div`
  position: absolute;
  background-color: ${palette.grayscale.shade1000};
  width: 10px;
  height: 10px;
  transform: rotate(45deg);
  z-index: -1;
`;

export function Tooltip({
  placement: preferredPlacement = 'top',
  content,
  children,
  style,
  className,
}: TooltipProps): React.ReactElement {
  const [open, setOpen] = useState(false);

  const arrowRef = useRef<HTMLDivElement>(null);

  const {
    context,
    x,
    y,
    reference,
    floating,
    strategy,
    placement,
    refs,
    middlewareData,
    update,
  } = useFloating({
    placement: preferredPlacement,
    open,
    onOpenChange: setOpen,
    middleware: [
      offset(6),
      flip(),
      shift({ padding: 5 }),
      arrow({ element: arrowRef }),
    ],
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useHover(context),
    useFocus(context),
    useRole(context, { role: 'tooltip' }),
    useDismiss(context),
  ]);

  useEffect(() => {
    if (!refs.reference.current || !refs.floating.current || !open) {
      return;
    }

    return autoUpdate(refs.reference.current, refs.floating.current, update);
  }, [refs.reference, refs.floating, update, open]);

  const staticSide = useMemo(() => {
    if (placement.startsWith('top')) {
      return 'bottom';
    }

    if (placement.startsWith('bottom')) {
      return 'top';
    }

    if (placement.startsWith('left')) {
      return 'right';
    }

    return 'left';
  }, [placement]);

  return (
    <>
      {cloneElement(children, getReferenceProps({ ref: reference }))}
      {open && (
        <Content
          {...getFloatingProps({
            ref: floating,
            style: {
              ...style,
              position: strategy,
              top: y ?? '',
              left: x ?? '',
            },
            className,
          })}
        >
          {content}
          <Arrow
            ref={arrowRef}
            style={{
              left: middlewareData.arrow?.x ?? '',
              top: middlewareData.arrow?.y ?? '',
              [staticSide]: -4,
            }}
          />
        </Content>
      )}
    </>
  );
}
